import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { Icon } from 'xm-spark';

import { triggerResizeEvent } from 'utils/html';
import { xmLocalStorage } from '@xmatters/common/utils/xmStorage';

import NavTooltip from '../NavTooltip';
import styles from './WideModeToggle.module.scss';

const IS_WIDE_STORE_KEY = 'xm.AllNavWasToggled';

const setStoredToggledWideState = isWide =>
  xmLocalStorage.setItem(IS_WIDE_STORE_KEY, `${isWide}`);

export const getStoredToggledWideState = () =>
  xmLocalStorage.getItem(IS_WIDE_STORE_KEY);

// Time in milliseconds until menu should be full size. Used to trigger a resize event for GWT components that need one:
const ANIMATION_TIME = 250;

const WideModeToggle = ({ isWideMode, onChange }) => {
  const intl = useIntl();

  const [userToggledWideMode, setUserToggledWideMode] = useState(
    getStoredToggledWideState() != null,
  );
  const toggleWideMode = () => onChange(!isWideMode);

  useEffect(() => {
    const onResize = debounce(() => {
      const shouldToggle =
        (!isWideMode && window.outerWidth > 1600) ||
        (isWideMode && window.outerWidth < 1600);
      if (shouldToggle) {
        toggleWideMode();
      }
    }, 150);

    if (!userToggledWideMode) {
      window.addEventListener('resize', onResize);
      onResize();
      return () => {
        window.removeEventListener('resize', onResize);
      };
    }
    return undefined;
  }, [isWideMode, userToggledWideMode]);

  const onClick = () => {
    setUserToggledWideMode(true);
    toggleWideMode();

    setStoredToggledWideState(!isWideMode);
    setTimeout(() => triggerResizeEvent(), ANIMATION_TIME);
  };

  const onKeyUp = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClick();
    }
  };

  return (
    <div className={styles.wrap}>
      <NavTooltip
        tooltipContent={
          <FormattedMessage id={isWideMode ? 'COLLAPSE' : 'EXPAND'} />
        }
      >
        <div
          className={styles.toggle}
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyUp={onKeyUp}
          aria-label={
            isWideMode
              ? intl.formatMessage({ id: 'COLLAPSE' })
              : intl.formatMessage({ id: 'EXPAND' })
          }
        >
          <Icon
            type="icon-double-chevron-right"
            className={classnames(styles.icon, {
              [styles.isWideMode]: isWideMode,
            })}
          />
        </div>
      </NavTooltip>
    </div>
  );
};

WideModeToggle.displayName = 'WideModeToggle';
WideModeToggle.propTypes = {
  isWideMode: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default WideModeToggle;
